/**
 * Physical edge length of the floor tile, in metres: Poly Haven lists
 * "Beige Wall 001" at 3000 × 3000 mm, and the maps are seamless at that
 * period. The plaster reads at its own scale — shrink the tile and the
 * trowel marks turn into a repeating pattern.
 *
 * The patch is not a fixed size, so the seam is not always out of frame: at
 * `STUDIO_FLOOR_MAX_RADIUS` (studioFloorExtent) the disc is 6 m across, two
 * tiles, so a large case does show the plaster repeating. Shrinking the tile
 * to hide that would repeat it more, not less; the fix, if it ever matters,
 * is a wider asset. This surface is uniform enough that the repeat is harder
 * to pick out than it was on the scuffed concrete before it.
 */
export const FLOOR_TILE_M = 3;

/**
 * Texture repeat for a circular floor patch, so that one tile covers
 * {@link FLOOR_TILE_M} of floor regardless of the patch's size.
 *
 * three's `CircleGeometry` maps UV 0..1 across the circle's full diameter
 * (`(x / radius + 1) / 2`), so the number of tiles across the patch is the
 * diameter over the tile length. Every map on the material carries its own
 * transform, so the same repeat has to be set on each.
 */
export function floorTextureRepeat(
  radiusM: number,
  tileM: number = FLOOR_TILE_M,
): number {
  return (radiusM * 2) / tileM;
}

/**
 * Texture offset that anchors the tiling to the floor's centre.
 *
 * `CircleGeometry` lays UV 0 at the rim, so with the repeat alone a texel's
 * world position depends on the radius: the centre of the patch sits at
 * texture coordinate `repeat / 2`, and growing the patch slides the whole
 * pattern under a case that has not moved. Subtracting half the repeat puts
 * the centre at coordinate 0 whatever the radius, which leaves the surface
 * looking like a floor the case stands on rather than a sheet being pulled
 * across it. Only the visible extent changes.
 *
 * The rim fade is unaffected: it is a separate `alphaMap` with its own UVs,
 * and it is *meant* to scale with the patch.
 */
export function floorTextureOffset(repeat: number): number {
  return -repeat / 2;
}
